import { Box, withStyles } from '@material-ui/core';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { QUIZ_STATUS_TYPES, SUBMISSION_STATUS_TYPES } from '../../config/configurations';
import * as QuestionsActions from '../../redux/actions/questions.actions';
import * as AnswersActions from '../../redux/actions/answers.actions';
import * as SubmissionsActions from '../../redux/actions/submissions.actions';
import { EDULAI_BLUE } from '../../styles/styleConsts';
import translations from '../../translations/i18next';
import MDButton from '../MDButton/MDButton';
import Spinner from '../Spinner/Spinner';
import QuizSubmissionLinearStepper from '../Stepper/QuizSubmissionLinearStepper';
import BaseQuizStatusView from '../QuizStatusesViews/BaseQuizStatusView';
import UserAnswers from '../Analytics/UserAnswers';
import QuizUserAnalytics from '../Analytics/QuizUserAnalytics';
import MDAccordion from '../MDAccordion/MDAccordion';
import QuizSubmissionReady from './QuizSubmissionReady';
import * as UtilsActions from '../../redux/actions/utils.actions';
import * as ModalsActions from '../../redux/actions/modal.actions';
import moment from 'moment';
import { isMobileBrowser } from '../../utils/utilsFunctions';
import { captureChart, createReportLinkAndDownload } from '../../utils/Report/ReportUtils';
import { UserPdfReport } from '../Analytics/Report/UserPdfReport';
import QuizFeedback from '../Stepper/QuizFeedback';
import { withRouter } from 'react-router-dom';
import { hasExpiredLicense } from '../../constants/license';
import { getReport } from '../../api/reportAPI';
import i18next from '../../translations/i18next';

const styles = () => ({
  container: {
    height: '100%',
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

const theme = createMuiTheme({
  palette: {
    primary: { 500: EDULAI_BLUE },
    secondary: { main: '#CC0033' }
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    useNextVariants: true
  }
});

class QuizSubmission extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      error: false,
      isFinalFeedbackShowed: false
    };
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    try {
      await dispatch(QuestionsActions.fetchQuestions());
      try {
        await dispatch(SubmissionsActions.fetchSubmissionsByUser());
      } catch (error) {}
      await dispatch(AnswersActions.fetchAnswers());
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false, error: true });
    }
  }

  async startNewSubmission() {
    const { dispatch } = this.props;
    this.setState({ isLoading: true });
    try {
      await dispatch(SubmissionsActions.startNewSubmission());
      try {
        await dispatch(SubmissionsActions.fetchSubmissionsByUser());
      } catch (error) {}
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false, error: true });
    }
  }

  async generatePdfAndDownload(data, user) {
    const {
      dispatch,
      quizzes: { selectedQuiz: quiz }
    } = this.props;

    try {
      dispatch(UtilsActions.setSpinnerVisible(true));
      const report = await getReport(i18next.language, user.id, quiz.id);
      const chart = await captureChart(document.getElementById(`chart-${user.id}`));
      const doc = <UserPdfReport data={{ ...data, user, chart, report: report.data }} />;
      await createReportLinkAndDownload(
        doc,
        `Edulai Report ${user.name} ${user.surname} ${moment().format('LLL')}.pdf`
      );
      dispatch(UtilsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisible(false));
      dispatch(
        ModalsActions.showModal('CREATE_PDF_ERROR_MODAL', {
          modalType: 'ERROR_ALERT',
          modalProps: {
            message: translations.t('analytics.errorCreatePDF')
          }
        })
      );
    }
  }

  get localStorageQuizKey() {
    const {
      quizzes: { selectedQuiz: quiz },
      user
    } = this.props;
    return `quiz_${quiz.id}_${user.id}`;
  }

  onCloseFinalFeedback = () => {
    this.setState({ isFinalFeedbackShowed: true });
    localStorage.setItem(this.localStorageQuizKey, 'concluded');
  };

  aksAI = () => {
    const { dispatch, history } = this.props;

    dispatch(ModalsActions.hideModal('VIEW_QUIZ_SUBMISSION_MODAL'));
    history.push('/coach-ai', { quiz: this.props.quizzes.selectedQuiz });
  };

  get isFinalFeedbackVisible() {
    return !this.state.isFinalFeedbackShowed && localStorage.getItem(this.localStorageQuizKey) !== 'concluded';
  }

  render() {
    const {
      classes,
      quizzes: { selectedQuiz: quiz },
      submissions: {
        data: { user: quizSubmissionsData }
      },
      onFinishQuiz,
      user,
      institutionType
    } = this.props;

    const { isLoading, error } = this.state;

    const quizSubmission = _.find(quizSubmissionsData, { quizId: quiz.id });
    const userSubmission = _.find(quizSubmissionsData, { ownerId: user.id, quizId: quiz.id });
    const hasPendingSubmissions =
      quizSubmission && quizSubmission.submissionStatusType === SUBMISSION_STATUS_TYPES.DRAFT;
    const hasTerminateAnswers = quiz && quiz.answers && _.size(quiz.answers) === _.size(quiz.questionsIds);
    const isPendingLastSubmissions =
      hasPendingSubmissions && hasTerminateAnswers && quiz.quizStatusType === QUIZ_STATUS_TYPES.RUNNING;

    return (
      <MuiThemeProvider theme={theme}>
        <Box className={classes.container} style={{ margin: 'auto' }}>
          {isLoading && (
            <Spinner
              customStyle={{ margin: 'auto' }}
              title={translations.t('general.loading')}
              hideLogo
              spinnerStyle={{ color: EDULAI_BLUE, marginTop: 10 }}
              titleStyle={{ color: '#80888d', marginTop: 5, fontSize: 12 }}
            />
          )}
          {!isLoading && error && (
            <div style={{ margin: 'auto', textAlign: 'center' }}>
              <h1>{translations.t('submissions.submissionLoadFailed')}</h1>
              <h2>{translations.t('submissions.pleaseReload')}</h2>
            </div>
          )}
          {!isLoading && <BaseQuizStatusView quiz={quiz} quizSubmission={quizSubmission} />}
          {!isLoading && !error && quiz.quizStatusType === QUIZ_STATUS_TYPES.RUNNING && !quizSubmission && (
            <QuizSubmissionReady
              quiz={quiz}
              institutionType={institutionType}
              onStart={() => this.startNewSubmission()}
            />
          )}
          {!isLoading &&
            !error &&
            quiz.quizStatusType === QUIZ_STATUS_TYPES.RUNNING &&
            quizSubmission &&
            quizSubmission.submissionStatusType === SUBMISSION_STATUS_TYPES.DRAFT &&
            !isPendingLastSubmissions && (
              <Box style={{ maxWidth: 1000 }}>
                <QuizSubmissionLinearStepper questions={quiz.questions} onFinishQuiz={() => onFinishQuiz()} />
              </Box>
            )}
          {!isLoading && !error && isPendingLastSubmissions && (
            <div style={{ textAlign: 'center', margin: 'auto' }}>
              <h3 style={{ textAlign: 'center', fontWeight: 'normal' }}>
                {translations.t('submissions.submissionCompleted')}
              </h3>
              <MDButton
                title={translations.t('submissions.concludeQuiz')}
                backgroundColor={EDULAI_BLUE}
                containerstyle={{ marginLeft: 30 }}
                onClick={() => onFinishQuiz()}
              />
            </div>
          )}
          {!isLoading &&
            !error &&
            (quiz.quizStatusType === QUIZ_STATUS_TYPES.RUNNING || quiz.quizStatusType === QUIZ_STATUS_TYPES.DONE) &&
            quizSubmission &&
            quizSubmission.submissionStatusType === SUBMISSION_STATUS_TYPES.SUBMITTED && (
              <Fragment>
                {this.isFinalFeedbackVisible ? (
                  <Box style={{ maxWidth: 1000 }}>
                    <QuizFeedback
                      last
                      onProceed={this.onCloseFinalFeedback}
                      onAskAI={!hasExpiredLicense(user) ? this.aksAI : undefined}
                    />
                  </Box>
                ) : (
                  <div style={{ paddingLeft: 150, paddingRight: 150 }}>
                    <h1 style={{ marginTop: 0, color: 'white', marginLeft: isMobileBrowser() ? 10 : 0 }}>
                      {quiz.name}
                    </h1>
                    <QuizUserAnalytics
                      hideSubSkills={false}
                      quiz={quiz.id}
                      user={user.id}
                      containerStyle={{ backgroundColor: 'white', borderRadius: 8, padding: 20 }}
                      onDownloadReport={(data) => this.generatePdfAndDownload(data, user)}
                      canDownloadReport={quiz.usersCanDownloadReport}
                    />
                    <MDAccordion
                      defaultExpanded
                      title={translations.t('questions.myAnswers')}
                      containerstyle={{ marginTop: 20, marginBottom: 20, borderRadius: 8 }}
                      accordionStyle={{ borderRadius: 8 }}
                      titleStyle={{ fontSize: 20 }}
                    >
                      <UserAnswers quiz={quiz} submission={userSubmission} />
                    </MDAccordion>
                  </div>
                )}
              </Fragment>
            )}
        </Box>
      </MuiThemeProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  quizzes: state.quizzes,
  questions: state.questions,
  submissions: state.submissions,
  user: state.user.data,
  institutionType: state.user.institutionType,
  skills: state.skills
});

export default connect(mapStateToProps)(withStyles(styles)(withRouter(QuizSubmission)));
